import merge from 'deepmerge';
import cloneDeep from 'lodash/cloneDeep';
import clamp from 'lodash/clamp';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import setWith from 'lodash/setWith';
import has from 'lodash/has';
import clone from 'lodash/clone';
import * as Actions from './AdvWorkflowActions';
import {
  ITERATOR_PRE_BEGIN,
  ITERATOR_POST_END,
  JUMP_TYPES,
  oppositeDirection,
  advance,
} from './AdvWorkflowHelpers';
import { isNumeric } from '../../../js/app/react/validators';

const saturate0 = x => clamp(x, 0, x);

export const selectFields = (state, workflowId, fields) => {
  if (!has(state, workflowId)) throw new Error('Missing workflow state');
  const previousWorkflowState = state[workflowId];
  if (isNil(previousWorkflowState) || typeof previousWorkflowState !== 'object')
    throw new Error('Invalid workflow state');

  if (!has(previousWorkflowState, 'currentClientId')) throw new Error('Missing client id');
  const { currentClientId: clientId } = previousWorkflowState;

  if (!isNumeric(clientId)) throw new Error('Invalid client id');

  if (!has(previousWorkflowState, clientId)) throw new Error('Missing workflow client state');
  const previousClientWorkflowState = previousWorkflowState[clientId];

  if (isNil(previousClientWorkflowState) || typeof previousClientWorkflowState !== 'object')
    throw new Error('Invalid workflow client state');
  const result = {
    clientId,
    previousWorkflowState,
    previousClientWorkflowState,
    fields: {},
  };
  fields.forEach(field => {
    if (!has(previousClientWorkflowState, field)) throw new Error('Invalid field');
    result.fields[field] = previousClientWorkflowState[field];
  });
  return result;
};

// shallow clone is required because redux will do a shallow compare
// to determine what are the props that changed
export const updateBranchWithValue = (object, path, value) =>
  clone(
    setWith(object, path, value, (nsValue, key, nsObjectArg) => {
      const nsObject = nsObjectArg;
      nsObject[key] = clone(nsValue);
      return nsObject[key];
    }),
  );

const jumpReducer = (state, { workflowId, ...action }) => {
  let { offset } = action;
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { data2 },
  } = selectFields(state, workflowId, ['data2']);

  let { iterator } = previousClientWorkflowState;
  const previousIterator = cloneDeep(iterator);
  const { tree: workflowTree } = previousClientWorkflowState;

  const direction = Math.sign(offset);
  const JUMP_DICTIONARY = {
    [-1]: JUMP_TYPES.BACKWARD,
    1: JUMP_TYPES.FORWARD,
  };

  while (offset !== 0) {
    iterator = advance(direction, workflowTree, cloneDeep(iterator));
    offset -= direction;
  }
  if (isNil(iterator)) throw new Error('Invalid iterator');

  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        jumpType: JUMP_DICTIONARY[direction],
        alpha: 0,
        previousIterator: cloneDeep(previousIterator),
        iterator,
        previousData2: cloneDeep(data2),
      },
    },
  };
};

const updateGraphDataReducer = (state, { workflowId, path, value }) => {
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { data: previousGraphData },
  } = selectFields(state, workflowId, ['data']);

  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        data: updateBranchWithValue(previousGraphData, path, value),
      },
    },
  };
};

const updateSupportDataReducer = (state, { workflowId, path, value }) => {
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { data2: previousSupportData },
  } = selectFields(state, workflowId, ['data2']);

  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        data2: updateBranchWithValue(previousSupportData, path, value),
      },
    },
  };
};

const jumpBackInHistoryReducer = (state, { workflowId }) => {
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { previousIterator, jumpType, data2 },
  } = selectFields(state, workflowId, ['previousIterator', 'jumpType', 'data2']);

  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        jumpType: oppositeDirection(jumpType),
        iterator: previousIterator,
        previousData2: cloneDeep(data2),
      },
    },
  };
};

const randomAccessJumpReducer = (state, { workflowId, iterator }) => {
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { tree, iterator: previousIterator, data2 },
  } = selectFields(state, workflowId, ['tree', 'iterator', 'data2']);

  if (iterator !== ITERATOR_PRE_BEGIN && iterator !== ITERATOR_POST_END) {
    if (!Array.isArray(iterator) || iterator.length === 0)
      throw new Error('Iterator must be a non empty array');
    let node = tree;
    for (let index = 0; index < iterator.length; index += 1) {
      node = node[iterator[index]];
    }
    if (!isEqual(node, {})) throw new Error('Iterator must point to a leaf');
  }
  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        jumpType: JUMP_TYPES.RANDOM_ACCESS,
        previousData2: cloneDeep(data2),
        previousIterator: cloneDeep(previousIterator),
        iterator,
      },
    },
  };
};

const depthFirstJump = (state, { workflowId, nextIterators }) => {
  const {
    clientId,
    previousClientWorkflowState,
    previousWorkflowState,
    fields: { tree, summaryStack, data2 },
  } = selectFields(state, workflowId, ['tree', 'summaryStack', 'data2']);

  nextIterators.forEach(iterator => {
    if (iterator !== ITERATOR_PRE_BEGIN && iterator !== ITERATOR_POST_END) {
      if (!Array.isArray(iterator) || iterator.length === 0)
        throw new Error('Iterator must be a non empty array');
      let node = tree;
      for (let index = 0; index < iterator.length; index += 1) {
        node = node[iterator[index]];
      }
      if (!isEqual(node, {})) throw new Error('Iterator must point to a leaf');
    }
  });

  // Compute the next summary stack
  const summaryStackCopy = clone(summaryStack);
  if (summaryStackCopy.length) {
    summaryStackCopy.pop();
  }
  for (let i = nextIterators.length - 1; i >= 0; i -= 1) {
    summaryStackCopy.push(nextIterators[i]);
  }
  return {
    ...state,
    [workflowId]: {
      ...previousWorkflowState,
      [clientId]: {
        ...previousClientWorkflowState,
        jumpType: JUMP_TYPES.RANDOM_ACCESS,
        summaryStack: summaryStackCopy,
        previousData2: cloneDeep(data2),
      },
    },
  };
};

const BASE_CLIENT_OBJECT = {
  jumpType: JUMP_TYPES.UNKNOWN,
  iterator: ITERATOR_PRE_BEGIN,
  tree: {},
  data: {},
  data2: {},
  backupData: {},
  previousData2: {},
  validation: {},
  spinner: 0,
  alpha: 0,
  iteratorHistory: [],
  summaryStack: [],
};
const INITIAL_STATE = {
  selectedGoalId: null,
};

export default (state = cloneDeep(INITIAL_STATE), action) => {
  const { type } = action;
  switch (type) {
    case Actions.ACQUIRE_LOADING: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { spinner },
      } = selectFields(state, action.workflowId, ['spinner']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            spinner: spinner + 1,
          },
        },
      };
    }
    case Actions.RELEASE_LOADING: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { spinner },
      } = selectFields(state, action.workflowId, ['spinner']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            spinner: saturate0(spinner - 1),
          },
        },
      };
    }
    case Actions.UPDATE_SUPPORT_DATA: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { data2 },
      } = selectFields(state, action.workflowId, ['data2']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            data2: merge(data2, action.data, {
              arrayMerge: (a, b) => b,
            }),
          },
        },
      };
    }
    case Actions.UPDATE_DATA: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { data },
      } = selectFields(state, action.workflowId, ['data']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            data: merge(data, action.data, {
              arrayMerge: (a, b) => b,
            }),
          },
        },
      };
    }
    case Actions.UPDATE_BACKUP_DATA: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { backupData },
      } = selectFields(state, action.workflowId, ['backupData']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            backupData: merge(backupData, action.backupData, {
              arrayMerge: (a, b) => b,
            }),
          },
        },
      };
    }
    case Actions.SET_DATA: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { populated },
      } = selectFields(state, action.workflowId, ['populated']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            data: action.data,
            populated: action.fullRefresh ? true : populated,
          },
        },
      };
    }
    case Actions.UPDATE_GRAPH_DATA_AND_SEQUENTIAL_ADVANCE: {
      return updateGraphDataReducer(jumpReducer(state, action), action);
    }
    case Actions.UPDATE_SUPPORT_DATA_AND_SEQUENTIAL_ADVANCE: {
      return updateSupportDataReducer(jumpReducer(state, action), action);
    }
    case Actions.RANDOM_ACCESS_JUMP: {
      return randomAccessJumpReducer(state, action);
    }
    case Actions.DEPTH_FIRST_JUMP: {
      return depthFirstJump(state, action);
    }
    case Actions.JUMP_BACK_IN_HISTORY: {
      return jumpBackInHistoryReducer(state, action);
    }
    case Actions.SET_POPULATED: {
      const { clientId, previousClientWorkflowState, previousWorkflowState } = selectFields(
        state,
        action.workflowId,
        [],
      );
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            populated: action.populated,
          },
        },
      };
    }
    case Actions.SET_BACKUP_DATA: {
      const { clientId, previousClientWorkflowState, previousWorkflowState } = selectFields(
        state,
        action.workflowId,
        [],
      );
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            backupData: action.backupData,
          },
        },
      };
    }
    case Actions.DO_BACKUP: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { data },
      } = selectFields(state, action.workflowId, ['data']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            backupData: cloneDeep(data),
          },
        },
      };
    }
    case Actions.UPDATE_VALIDATION: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { validation },
      } = selectFields(state, action.workflowId, ['validation']);
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            validation: merge(validation, action.validation, {
              arrayMerge: (a, b) => b,
            }),
          },
        },
      };
    }

    case Actions.INITIALIZE_SECTION: {
      return {
        ...state,
        [action.workflowId]: {
          currentClientId: action.clientId,
          [action.clientId]: cloneDeep(BASE_CLIENT_OBJECT),
        },
      };
    }

    case Actions.CLEAR_WORKFLOW: {
      return {
        ...cloneDeep(INITIAL_STATE),
        selectedGoalId: action.selectedGoalId,
      };
    }

    case Actions.JUMP: {
      return jumpReducer(state, action);
    }
    case Actions.PUSH_ITERATOR_TO_HISTORY: {
      const MAX_ELEMENTS_IN_HISTORY = 5;
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { iteratorHistory, iterator },
      } = selectFields(state, action.workflowId, ['iteratorHistory', 'iterator']);

      const clonedIteratorHistory = cloneDeep(iteratorHistory);
      clonedIteratorHistory.push(cloneDeep(iterator));
      if (clonedIteratorHistory.length > MAX_ELEMENTS_IN_HISTORY) {
        clonedIteratorHistory.shift();
      }
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            iteratorHistory: clonedIteratorHistory,
          },
        },
      };
    }
    case Actions.BUMP_PROGRESS_BAR: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { alpha },
      } = selectFields(state, action.workflowId, ['alpha']);
      const normalizedAlpha = clamp(alpha, 0, 1);
      const remaining = 1.0 - normalizedAlpha;
      const random = Math.random() * 0.66;
      const add = random * remaining;
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            alpha: normalizedAlpha + add,
          },
        },
      };
    }
    case Actions.SET_ALPHA: {
      const { clientId, previousClientWorkflowState, previousWorkflowState } = selectFields(
        state,
        action.workflowId,
        [],
      );
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            alpha: action.alpha,
          },
        },
      };
    }
    case Actions.POP_ITERATOR_FROM_HISTORY: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { iteratorHistory, iterator: previousIterator, data2 },
      } = selectFields(state, action.workflowId, ['iteratorHistory', 'iterator', 'data2']);

      const clonedIteratorHistory = cloneDeep(iteratorHistory);
      const iterator = clonedIteratorHistory.pop();
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            previousData2: cloneDeep(data2),
            previousIterator: cloneDeep(previousIterator),
            iterator,
            iteratorHistory: clonedIteratorHistory,
          },
        },
      };
    }
    case Actions.EXTEND_TREE: {
      const {
        clientId,
        previousClientWorkflowState,
        previousWorkflowState,
        fields: { tree },
      } = selectFields(state, action.workflowId, ['tree']);

      const degenerateTree = {};
      let node = degenerateTree;
      for (let i = 0; i < action.branch.length; i += 1) {
        node[action.branch[i]] = {};
        node = node[action.branch[i]];
      }
      return {
        ...state,
        [action.workflowId]: {
          ...previousWorkflowState,
          [clientId]: {
            ...previousClientWorkflowState,
            tree: merge(tree, degenerateTree, {
              arrayMerge: (a, b) => b,
            }),
          },
        },
      };
    }

    default: {
      return state;
    }
  }
};
